import { useEffect, useMemo, useState, type FormEvent } from "react";
import { addDoc, collection, doc, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { paths, type OrderedItem, type OrderTrigger, type PricebookItem } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { textMatches } from "../lib/filter";
import { useAuth } from "../hooks/useAuth";

// Parts on order for this job. Any "ordered" row = a parts hold: the job parks
// in the pipeline's "Waiting on parts" bucket until everything is received.
// Planned rows are staged procurement — they wait on a date or on other parts
// arriving, flip to "ready" server-side, and only hold the job once ordered.

type Row = OrderedItem & { id: string };

type When = "now" | "date" | "afterItems";

function triggerText(t: OrderTrigger | null, byId: Map<string, Row>): string {
  if (!t) return "";
  if (t.kind === "date") {
    if (t.at) return `order on ${new Date(t.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    if (t.offsetDays != null) return `order ${t.offsetDays}d after job start`;
    return "";
  }
  const names = t.triggerItemIds.map((id) => byId.get(id)?.description ?? "a part");
  const shown = names.slice(0, 2).join(", ");
  return `order after ${shown}${names.length > 2 ? ` +${names.length - 2} more` : ""} arrive${names.length === 1 ? "s" : ""}`;
}

export function OrderedParts({ tenantId, jobId }: { tenantId: string; jobId: string }) {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ description: "", vendor: "", quantity: "1", expected: "" });
  const [busy, setBusy] = useState(false);
  const [when, setWhen] = useState<When>("now");
  const [orderOn, setOrderOn] = useState("");
  const [offsetDays, setOffsetDays] = useState("");
  const [afterIds, setAfterIds] = useState<Set<string>>(new Set());
  const [pricebook, setPricebook] = useState<Array<{ id: string; name: string; sku: string }>>([]);
  const [linkedItemId, setLinkedItemId] = useState<string | null>(null);

  // Pricebook loads only while the order form is open — linking an item means
  // receiving will stock the shelf automatically.
  useEffect(() => {
    if (!open) return;
    return onSnapshot(collection(db, paths.tenantPricebook(tenantId)), (snap) =>
      setPricebook(
        snap.docs
          .filter((d) => !(d.data() as PricebookItem).archivedAt)
          .map((d) => ({ id: d.id, name: (d.get("name") as string) ?? "", sku: (d.get("sku") as string) ?? "" })),
      ),
    );
  }, [open, tenantId]);

  const itemMatches = useMemo(
    () =>
      form.description.trim() && !linkedItemId
        ? pricebook.filter((p) => textMatches(form.description, p.name, p.sku)).slice(0, 5)
        : [],
    [pricebook, form.description, linkedItemId],
  );

  useEffect(() => {
    return onSnapshot(
      query(collection(db, paths.tenantJobOrderedItems(tenantId, jobId)), orderBy("orderedAt", "desc")),
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as OrderedItem) }))),
    );
  }, [tenantId, jobId]);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!user || !form.description.trim()) return;
    let trigger: OrderTrigger | null = null;
    if (when === "date") {
      const at = orderOn ? new Date(`${orderOn}T12:00`).getTime() : null;
      const off = offsetDays.trim() ? Math.max(0, Math.round(parseFloat(offsetDays) || 0)) : null;
      if (at === null && off === null) return;
      trigger = { kind: "date", at, offsetDays: at === null ? off : null };
    } else if (when === "afterItems") {
      if (afterIds.size === 0) return;
      trigger = { kind: "afterItems", triggerItemIds: [...afterIds] };
    }
    setBusy(true);
    try {
      const item: OrderedItem = {
        tenantId,
        jobId,
        description: form.description.trim(),
        vendor: form.vendor.trim(),
        vendorId: null,
        itemId: linkedItemId,
        quantity: Math.max(1, parseFloat(form.quantity) || 1),
        expectedAt: form.expected ? new Date(`${form.expected}T12:00`).getTime() : null,
        status: when === "now" ? "ordered" : "planned",
        trigger,
        readyAt: null,
        orderedByUid: user.uid,
        orderedAt: Date.now(),
        receivedAt: null,
      };
      await addDoc(collection(db, paths.tenantJobOrderedItems(tenantId, jobId)), item);
      setForm({ description: "", vendor: "", quantity: "1", expected: "" });
      setLinkedItemId(null);
      setWhen("now");
      setOrderOn("");
      setOffsetDays("");
      setAfterIds(new Set());
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  async function receive(id: string) {
    await updateDoc(doc(db, `${paths.tenantJobOrderedItems(tenantId, jobId)}/${id}`), {
      status: "received",
      receivedAt: Date.now(),
    });
  }

  async function placeOrder(id: string) {
    await updateDoc(doc(db, `${paths.tenantJobOrderedItems(tenantId, jobId)}/${id}`), {
      status: "ordered",
      orderedAt: Date.now(),
    });
  }

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const openCount = rows.filter((r) => r.status === "ordered").length;
  const readyCount = rows.filter((r) => r.status === "ready").length;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">
          Ordered parts ({rows.length})
          {readyCount > 0 ? <span className="ml-2 text-xs font-normal text-brandorange-dark">{readyCount} ready to order</span> : null}
          {openCount > 0 ? <span className="ml-2 text-xs font-normal text-brandorange-dark">{openCount} on order — job waits</span> : null}
        </CardTitle>
        <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setOpen((v) => !v)}>
          {open ? "Cancel" : "+ Order part"}
        </Button>
      </div>

      {open ? (
        <form onSubmit={add} className="mt-2 flex flex-wrap items-end gap-2 rounded-lg bg-cream p-3">
          <div className="min-w-48 flex-1">
            <Label>What</Label>
            <Input
              required
              value={form.description}
              onChange={(e) => {
                setForm({ ...form, description: e.target.value });
                setLinkedItemId(null);
              }}
              placeholder="16x7 9100 LP door, white"
            />
            {linkedItemId ? (
              <p className="mt-1 text-[11px] text-success">
                Linked to pricebook — receiving will add it to stock.{" "}
                <button type="button" className="underline" onClick={() => setLinkedItemId(null)}>
                  unlink
                </button>
              </p>
            ) : itemMatches.length > 0 ? (
              <div className="mt-1 rounded-lg border border-steel/20 bg-white">
                {itemMatches.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="block w-full px-2 py-1 text-left text-xs text-ink hover:bg-cream"
                    onClick={() => {
                      setLinkedItemId(p.id);
                      setForm((f) => ({ ...f, description: p.name }));
                    }}
                  >
                    {p.name}
                    {p.sku ? <span className="ml-1 text-steel">· {p.sku}</span> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div>
            <Label>Vendor</Label>
            <Input className="w-36" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
          </div>
          <div>
            <Label>Qty</Label>
            <Input type="number" min="1" className="w-16" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          </div>
          <div>
            <Label>Expected</Label>
            <Input type="date" className="w-36" value={form.expected} onChange={(e) => setForm({ ...form, expected: e.target.value })} />
          </div>

          {/* Staged procurement: order now, on a date, or once other parts land. */}
          <div className="w-full">
            <div className="flex gap-1">
              {(
                [
                  ["now", "Order now"],
                  ["date", "On a date"],
                  ["afterItems", "After parts arrive"],
                ] as Array<[When, string]>
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  className={cn(
                    "rounded-full px-2.5 py-1 text-xs",
                    when === k ? "bg-ink text-white" : "bg-white text-steel hover:text-ink",
                  )}
                  onClick={() => setWhen(k)}
                >
                  {label}
                </button>
              ))}
            </div>
            {when === "date" ? (
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <div>
                  <Label>Order on</Label>
                  <Input type="date" className="w-36" value={orderOn} onChange={(e) => setOrderOn(e.target.value)} />
                </div>
                <span className="pb-2 text-xs text-steel">or</span>
                <div>
                  <Label>Days after job start</Label>
                  <Input
                    type="number"
                    min="0"
                    className="w-24"
                    placeholder="21"
                    value={offsetDays}
                    onChange={(e) => setOffsetDays(e.target.value)}
                  />
                </div>
              </div>
            ) : null}
            {when === "afterItems" ? (
              <div className="mt-2">
                <Label>Order once these arrive</Label>
                {rows.filter((r) => r.status !== "received").length === 0 ? (
                  <p className="text-xs text-steel">No other parts on this job yet — add the earlier parts first.</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {rows
                      .filter((r) => r.status !== "received")
                      .map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          className={cn(
                            "max-w-56 truncate rounded-full px-2.5 py-1 text-xs",
                            afterIds.has(r.id) ? "bg-brandorange text-white" : "bg-white text-steel hover:text-ink",
                          )}
                          onClick={() =>
                            setAfterIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(r.id)) next.delete(r.id);
                              else next.add(r.id);
                              return next;
                            })
                          }
                        >
                          {r.description}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <Button type="submit" busy={busy}>
            {when === "now" ? "Order" : "Plan"}
          </Button>
        </form>
      ) : null}

      <div className="mt-2">
        {rows.map((r) => (
          <div
            key={r.id}
            className={cn(
              "flex items-center justify-between gap-2 border-b border-steel/10 py-1.5 last:border-0",
              r.status === "ready" && "-mx-2 rounded-lg border-0 bg-brandorange/10 px-2",
            )}
          >
            <span className={cn("min-w-0 truncate text-sm text-ink", r.status === "planned" && "text-steel")}>
              {r.quantity > 1 ? `${r.quantity} × ` : ""}
              {r.description}
              <span className="ml-1 text-xs text-steel">
                {[
                  r.vendor,
                  r.status === "planned" ? triggerText(r.trigger, byId) : "",
                  r.status === "ready" ? "ready to order" : "",
                  r.expectedAt && r.status === "ordered" ? `exp. ${new Date(r.expectedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            {r.status === "planned" ? (
              <Button variant="ghost" className="shrink-0 px-2.5 py-1 text-xs" onClick={() => void placeOrder(r.id)}>
                Order now
              </Button>
            ) : r.status === "ready" ? (
              <Button className="shrink-0 px-2.5 py-1 text-xs" onClick={() => void placeOrder(r.id)}>
                Mark ordered
              </Button>
            ) : r.status === "ordered" ? (
              <Button className="shrink-0 px-2.5 py-1 text-xs" onClick={() => void receive(r.id)}>
                Received
              </Button>
            ) : (
              <span className={cn("shrink-0 rounded-full bg-success/15 px-2 py-0.5 text-xs text-success")}>received</span>
            )}
          </div>
        ))}
        {rows.length === 0 && !open ? (
          <p className="py-1 text-xs text-steel">Nothing on order for this job.</p>
        ) : null}
      </div>
    </Card>
  );
}
